import { ReportStatus } from "../../../recoil/types";
import { formatTrackerDate } from "../../../utils/dateUtils";

/**
 * The four steps of the patient results journey shown in the tracker.
 *
 * Order matters: this array defines progression from first to last and is used
 * to compute which steps are complete/current/upcoming.
 */
export enum TrackerStepId {
  KitReceived = "kit_received",
  LabProcessing = "lab_processing",
  ProviderReviewing = "provider_reviewing",
  ResultsReady = "results_ready",
}

export const TRACKER_STEP_ORDER: readonly TrackerStepId[] = [
  TrackerStepId.KitReceived,
  TrackerStepId.LabProcessing,
  TrackerStepId.ProviderReviewing,
  TrackerStepId.ResultsReady,
] as const;

export const TRACKER_STEP_LABELS: Record<TrackerStepId, string> = {
  [TrackerStepId.KitReceived]: "Kit received",
  [TrackerStepId.LabProcessing]: "Lab processing",
  [TrackerStepId.ProviderReviewing]: "Provider reviewing",
  [TrackerStepId.ResultsReady]: "Results ready",
};

export type TrackerStepState = "complete" | "current" | "upcoming";

export interface TrackerStepView {
  id: TrackerStepId;
  label: string;
  /** Real date for this step (MM/DD/YYYY), or "" when no real source exists. */
  date: string;
  /** Secondary line under the label (e.g. "Contact Provider" / "Results est. ..."). */
  subtext: string;
  state: TrackerStepState;
}

export interface DeriveTrackerInput {
  status: ReportStatus;
  providerReleasedTime: string | null;
  dateReceived: string;
  estimatedDate: string | null;
}

const READY_STATUSES: ReadonlySet<ReportStatus> = new Set<ReportStatus>([
  "Final",
  "Corrected",
]);

/**
 * Determine which step a report is currently on, using only real fields.
 *
 * No time-based heuristics or fabricated phases:
 * - Final/Corrected -> Results ready
 * - has `provider_released_time` -> Provider reviewing
 * - status Processing -> Lab processing
 * - otherwise (status Ordered: received/queued, not yet in lab) -> Kit received
 */
export const deriveCurrentStep = (input: DeriveTrackerInput): TrackerStepId => {
  if (READY_STATUSES.has(input.status)) {
    return TrackerStepId.ResultsReady;
  }
  if (input.providerReleasedTime) {
    return TrackerStepId.ProviderReviewing;
  }
  if (input.status === "Processing") {
    return TrackerStepId.LabProcessing;
  }
  return TrackerStepId.KitReceived;
};

const getStepState = (
  stepIndex: number,
  currentIndex: number,
): TrackerStepState => {
  if (stepIndex < currentIndex) {
    return "complete";
  }
  if (stepIndex === currentIndex) {
    return "current";
  }
  return "upcoming";
};

/**
 * Resolve the real date string for a given step.
 *
 * Kit received and Lab processing both use `date_received` (the kit-received
 * date the design shows for both), Provider reviewing uses the provider release
 * timestamp, and Results ready uses the estimated results date.
 */
const getStepDate = (
  stepId: TrackerStepId,
  input: DeriveTrackerInput,
): string => {
  switch (stepId) {
    case TrackerStepId.KitReceived:
    case TrackerStepId.LabProcessing:
      return formatTrackerDate(input.dateReceived);
    case TrackerStepId.ProviderReviewing:
      return formatTrackerDate(input.providerReleasedTime);
    case TrackerStepId.ResultsReady:
      return formatTrackerDate(input.estimatedDate);
    default:
      return "";
  }
};

export interface MinimalTrackerView {
  currentStepId: TrackerStepId;
  /** Number of segments to fill (1-4), i.e. current step index + 1. */
  filledSegments: number;
  totalSegments: number;
  label: string;
  date: string;
  subtext: string;
}

/**
 * Build the single current-step view for the Minimal tracker.
 *
 * On the show-date variants, the Minimal style shows the estimated date under
 * the current step - except on Provider reviewing, where it shows "Contact
 * Provider" instead. The hide-date variants render no secondary line at all.
 */
export const buildMinimalTracker = (
  input: DeriveTrackerInput,
  showEstimatedDate: boolean,
): MinimalTrackerView => {
  const currentStepId = deriveCurrentStep(input);
  const currentIndex = TRACKER_STEP_ORDER.indexOf(currentStepId);

  // Subtext (Contact Provider / Results est.) only appears on the show-date
  // variants; the hide-date variants render no secondary line.
  let subtext = "";
  if (showEstimatedDate) {
    if (currentStepId === TrackerStepId.ProviderReviewing) {
      subtext = "Contact Provider";
    } else {
      const estimated = formatTrackerDate(input.estimatedDate);
      subtext = estimated ? `Results est. ${estimated}` : "";
    }
  }

  const date =
    currentStepId === TrackerStepId.ProviderReviewing
      ? formatTrackerDate(input.providerReleasedTime)
      : formatTrackerDate(input.dateReceived);

  return {
    currentStepId,
    filledSegments: currentIndex + 1,
    totalSegments: TRACKER_STEP_ORDER.length,
    label: TRACKER_STEP_LABELS[currentStepId],
    date,
    subtext,
  };
};

/**
 * Build the full list of step view-models for rendering both tracker styles.
 *
 * @param input Real report fields driving the journey.
 * @param showEstimatedDate Whether secondary subtext lines are shown (the "show
 *   date" experiment variants): "Results est. ..." on Results ready and "Contact
 *   Provider" on Provider reviewing. The hide-date variants render no subtext.
 */
export const buildTrackerSteps = (
  input: DeriveTrackerInput,
  showEstimatedDate: boolean,
): TrackerStepView[] => {
  const currentStep = deriveCurrentStep(input);
  const currentIndex = TRACKER_STEP_ORDER.indexOf(currentStep);

  return TRACKER_STEP_ORDER.map((stepId, index) => {
    const state = getStepState(index, currentIndex);

    // Subtext (Contact Provider / Results est.) only appears on the show-date
    // variants; the hide-date variants render no secondary line.
    let subtext = "";
    if (showEstimatedDate) {
      if (stepId === TrackerStepId.ProviderReviewing && state !== "upcoming") {
        subtext = "Contact Provider";
      } else if (stepId === TrackerStepId.ResultsReady) {
        const estimated = formatTrackerDate(input.estimatedDate);
        subtext = estimated ? `Results est. ${estimated}` : "";
      }
    }

    // The estimated-date line lives in `subtext` for Results ready; the inline
    // `date` column is only for steps with a concrete event date.
    const date =
      stepId === TrackerStepId.ResultsReady ? "" : getStepDate(stepId, input);

    return {
      id: stepId,
      label: TRACKER_STEP_LABELS[stepId],
      date,
      subtext,
      state,
    };
  });
};
